// Shared Razorpay Checkout flow for every purchase on the platform.
//
// Every purchase endpoint returns either
//   { payment_required: false, ...result }   → mock mode / already granted
//   { payment_required: true, checkout: {…} } → open Razorpay Checkout
// completePurchase() handles both: it resolves with the final result after the
// grant is confirmed (for live payments, after signature verification).

import { apiClient } from "@/lib/api";

let scriptPromise = null;
function loadRazorpayScript() {
  if (window.Razorpay) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = "https://checkout.razorpay.com/v1/checkout.js";
      s.onload = resolve;
      s.onerror = () => { scriptPromise = null; reject(new Error("Couldn't load the payment window. Check your connection and try again.")); };
      document.body.appendChild(s);
    });
  }
  return scriptPromise;
}

export async function completePurchase(purchaseResponse, { user } = {}) {
  const data = purchaseResponse?.data ?? purchaseResponse;
  if (!data?.payment_required) return data; // mock mode — already granted

  await loadRazorpayScript();
  const c = data.checkout;
  return new Promise((resolve, reject) => {
    const rzp = new window.Razorpay({
      key: c.key,
      order_id: c.order_id,
      amount: c.amount,
      currency: c.currency || "INR",
      name: c.name || "Wishire",
      description: c.description,
      prefill: { name: user?.name || "", email: user?.email || "", contact: user?.phone || "" },
      theme: { color: "#C2410C" },
      modal: { ondismiss: () => reject(new Error("Payment cancelled.")) },
      handler: async (resp) => {
        try {
          const { data: verified } = await apiClient.post("/payments/verify", resp);
          resolve(verified);
        } catch (e) {
          reject(new Error(e?.response?.data?.detail || "Payment verification failed — if money was deducted it will be honoured automatically within a few minutes."));
        }
      },
    });
    rzp.on("payment.failed", (resp) => {
      rzp.close();
      reject(new Error(resp?.error?.description || "Payment failed. You have not been charged."));
    });
    rzp.open();
  });
}
